import React, { useState } from 'react';
import { webEnrichmentService } from '../../web_enrichment/services/web-enrichment.service';
import { WebEnrichmentData } from '../../web_enrichment/types';

interface Props {
  webEnrichmentData: WebEnrichmentData;
  companyId: number;
  accentColor?: string;
  onSaved: (updated: WebEnrichmentData) => void;
  onCancel: () => void;
}

// Scalar fields → input/textarea
const SCALAR_FIELDS: { key: string; label: string; multiline?: boolean }[] = [
  { key: 'company_name', label: 'Company Name' },
  { key: 'tagline', label: 'Tagline' },
  { key: 'mission', label: 'Mission', multiline: true },
  { key: 'vision', label: 'Vision', multiline: true },
  { key: 'description', label: 'Description', multiline: true },
  { key: 'about', label: 'About', multiline: true },
  { key: 'industry', label: 'Industry' },
  { key: 'headquarters_location', label: 'Headquarters Location' },
  { key: 'contact_email', label: 'Contact Email' },
  { key: 'phone', label: 'Phone' },
];

// String array fields
const ARRAY_FIELDS: { key: string; label: string }[] = [
  { key: 'products', label: 'Products' },
  { key: 'product_categories', label: 'Product Categories' },
  { key: 'sectors', label: 'Sectors' },
  { key: 'technologies', label: 'Technologies' },
  { key: 'innovations', label: 'Innovations' },
];

export const WebEnrichmentEditMode: React.FC<Props> = ({
  webEnrichmentData,
  companyId,
  accentColor = '#3B82F6',
  onSaved,
  onCancel,
}) => {
  const d = webEnrichmentData.extracted_data ?? {};

  // Scalar state — keyed by field name
  const [scalars, setScalars] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const { key } of SCALAR_FIELDS) {
      init[key] = typeof d[key] === 'string' ? d[key] : '';
    }
    return init;
  });

  // String array state
  const [arrays, setArrays] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const { key } of ARRAY_FIELDS) {
      init[key] = Array.isArray(d[key]) ? [...d[key]] : [];
    }
    return init;
  });

  // Leadership team: [{name, title}]
  const [leaders, setLeaders] = useState<{ name: string; title: string }[]>(() => {
    const raw = d.leadership_team;
    if (!Array.isArray(raw)) return [];
    return raw.map((l: any) => ({
      name: typeof l === 'string' ? l : (l?.name ?? ''),
      title: typeof l === 'string' ? '' : (l?.title ?? ''),
    }));
  });

  // Social media: [{platform, url}] for editing; persisted as object
  const [socialRows, setSocialRows] = useState<{ platform: string; url: string }[]>(() => {
    const sm = d.social_media;
    if (!sm || typeof sm !== 'object') return [];
    return Object.entries(sm).map(([platform, url]) => ({
      platform,
      url: String(url),
    }));
  });

  // additional_info as raw JSON string
  const [additionalInfoRaw, setAdditionalInfoRaw] = useState<string>(() => {
    if (d.additional_info == null) return '';
    try {
      return JSON.stringify(d.additional_info, null, 2);
    } catch {
      return '';
    }
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ─── helpers ───────────────────────────────────────────────────────────────

  const setArrayItem = (field: string, idx: number, val: string) => {
    setArrays((prev) => {
      const copy = [...prev[field]];
      copy[idx] = val;
      return { ...prev, [field]: copy };
    });
  };

  const removeArrayItem = (field: string, idx: number) => {
    setArrays((prev) => {
      const copy = prev[field].filter((_, i) => i !== idx);
      return { ...prev, [field]: copy };
    });
  };

  const addArrayItem = (field: string) => {
    setArrays((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const setLeader = (idx: number, key: 'name' | 'title', val: string) => {
    setLeaders((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: val };
      return copy;
    });
  };

  const removeLeader = (idx: number) => {
    setLeaders((prev) => prev.filter((_, i) => i !== idx));
  };

  const addLeader = () => {
    setLeaders((prev) => [...prev, { name: '', title: '' }]);
  };

  const setSocialRow = (idx: number, key: 'platform' | 'url', val: string) => {
    setSocialRows((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: val };
      return copy;
    });
  };

  const removeSocialRow = (idx: number) => {
    setSocialRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const addSocialRow = () => {
    setSocialRows((prev) => [...prev, { platform: '', url: '' }]);
  };

  // ─── save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      const patch: Record<string, any> = {};

      // Scalars — send null to clear, string to set
      for (const { key } of SCALAR_FIELDS) {
        const orig = typeof d[key] === 'string' ? d[key] : '';
        const cur = scalars[key];
        if (cur !== orig) {
          patch[key] = cur === '' ? null : cur;
        }
      }

      // String arrays
      for (const { key } of ARRAY_FIELDS) {
        const cleaned = arrays[key].map((s) => s.trim()).filter(Boolean);
        const origArr = Array.isArray(d[key]) ? d[key] : [];
        if (JSON.stringify(cleaned) !== JSON.stringify(origArr)) {
          patch[key] = cleaned.length > 0 ? cleaned : null;
        }
      }

      // Leadership team
      const cleanedLeaders = leaders
        .filter((l) => l.name.trim())
        .map((l) => ({ name: l.name.trim(), title: l.title.trim() }));
      const origLeaders = Array.isArray(d.leadership_team) ? d.leadership_team : [];
      if (JSON.stringify(cleanedLeaders) !== JSON.stringify(origLeaders)) {
        patch.leadership_team = cleanedLeaders.length > 0 ? cleanedLeaders : null;
      }

      // Social media
      const socialObj: Record<string, string> = {};
      for (const row of socialRows) {
        const p = row.platform.trim();
        const u = row.url.trim();
        if (p && u) socialObj[p] = u;
      }
      const origSocial = d.social_media ?? {};
      if (JSON.stringify(socialObj) !== JSON.stringify(origSocial)) {
        patch.social_media = Object.keys(socialObj).length > 0 ? socialObj : null;
      }

      // additional_info
      if (additionalInfoRaw.trim() === '') {
        if (d.additional_info != null) patch.additional_info = null;
      } else {
        try {
          const parsed = JSON.parse(additionalInfoRaw);
          if (JSON.stringify(parsed) !== JSON.stringify(d.additional_info ?? {})) {
            patch.additional_info = parsed;
          }
        } catch {
          setSaveError('additional_info is not valid JSON');
          setIsSaving(false);
          return;
        }
      }

      if (Object.keys(patch).length === 0) {
        onCancel();
        return;
      }

      const updated = await webEnrichmentService.patchExtractedData(companyId, patch);
      onSaved(updated);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── shared input styles ───────────────────────────────────────────────────

  const inputCls =
    'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent bg-white';
  const inputStyle = { '--tw-ring-color': accentColor } as React.CSSProperties;

  return (
    <div className="space-y-6">
      {/* Scalar fields */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Overview</h4>
        <div className="space-y-3">
          {SCALAR_FIELDS.map(({ key, label, multiline }) => (
            <div key={key}>
              <label className="text-xs font-medium text-gray-500 block mb-1">{label}</label>
              {multiline ? (
                <textarea
                  className={inputCls}
                  style={inputStyle}
                  rows={3}
                  value={scalars[key]}
                  onChange={(e) => setScalars((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder={`Enter ${label.toLowerCase()}…`}
                />
              ) : (
                <input
                  type="text"
                  className={inputCls}
                  style={inputStyle}
                  value={scalars[key]}
                  onChange={(e) => setScalars((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder={`Enter ${label.toLowerCase()}…`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* String array fields */}
      {ARRAY_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">{label}</h4>
          <div className="space-y-2">
            {arrays[key].map((item, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  className={`${inputCls} flex-1`}
                  style={inputStyle}
                  value={item}
                  onChange={(e) => setArrayItem(key, idx, e.target.value)}
                  placeholder={`${label} item…`}
                />
                <button
                  type="button"
                  onClick={() => removeArrayItem(key, idx)}
                  className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  title="Remove"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addArrayItem(key)}
              className="text-xs font-medium transition-colors hover:opacity-80"
              style={{ color: accentColor }}
            >
              + Add item
            </button>
          </div>
        </div>
      ))}

      {/* Leadership team */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Leadership Team</h4>
        <div className="space-y-3">
          {leaders.map((leader, idx) => (
            <div key={idx} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
              <div className="flex gap-2 items-start">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className={inputCls}
                    style={inputStyle}
                    value={leader.name}
                    onChange={(e) => setLeader(idx, 'name', e.target.value)}
                    placeholder="Full name"
                  />
                  <input
                    type="text"
                    className={inputCls}
                    style={inputStyle}
                    value={leader.title}
                    onChange={(e) => setLeader(idx, 'title', e.target.value)}
                    placeholder="Title / role"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeLeader(idx)}
                  className="text-gray-400 hover:text-red-500 transition-colors mt-1 flex-shrink-0"
                  title="Remove leader"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addLeader}
            className="text-xs font-medium transition-colors hover:opacity-80"
            style={{ color: accentColor }}
          >
            + Add leader
          </button>
        </div>
      </div>

      {/* Social media */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Social Media</h4>
        <div className="space-y-2">
          {socialRows.map((row, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input
                type="text"
                className={`${inputCls} w-32 flex-shrink-0`}
                style={inputStyle}
                value={row.platform}
                onChange={(e) => setSocialRow(idx, 'platform', e.target.value)}
                placeholder="Platform"
              />
              <input
                type="url"
                className={`${inputCls} flex-1`}
                style={inputStyle}
                value={row.url}
                onChange={(e) => setSocialRow(idx, 'url', e.target.value)}
                placeholder="URL"
              />
              <button
                type="button"
                onClick={() => removeSocialRow(idx)}
                className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                title="Remove"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addSocialRow}
            className="text-xs font-medium transition-colors hover:opacity-80"
            style={{ color: accentColor }}
          >
            + Add social link
          </button>
        </div>
      </div>

      {/* Additional info */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Additional Info (JSON)</h4>
        <textarea
          className={inputCls}
          style={inputStyle}
          rows={4}
          value={additionalInfoRaw}
          onChange={(e) => setAdditionalInfoRaw(e.target.value)}
          placeholder='{"key": "value"}'
          spellCheck={false}
        />
        <p className="text-xs text-gray-400 mt-1">Leave blank to clear. Must be valid JSON.</p>
      </div>

      {/* Error */}
      {saveError && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-100">
          <p className="text-xs text-red-600">{saveError}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: accentColor }}
        >
          {isSaving && (
            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
